import { useForm } from '@tanstack/react-form';
import {
    EmailAuthProvider,
    reauthenticateWithCredential,
    signInWithEmailAndPassword,
    signOut,
} from 'firebase/auth';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { AuthLayout } from '@workspace/layout';
import { CardContent, Label } from '@workspace/shadcn';
import {
    FieldMessage,
    FormMessage,
    PasswordInput,
    TheButton,
    client,
} from '@workspace/ui';

import { auth } from '../firebase.tsx';

const schema = z
  .object({
    currentPassword: z
      .string()
      .min(1, { message: 'Current password is required' }),
    password: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters' }),
    confirmPassword: z
      .string()
      .min(8, { message: 'Password confirmation is required' }),
  })
  .refine((value) => value.password === value.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export default function ChangePassword() {
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: {
      currentPassword: '',
      password: '',
      confirmPassword: '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async (props) => {
        const issue = (field: string, message: string) =>
          ({ [field]: [{ message }] }) as Record<
            string,
            { message: string }[]
          >;

        const user = auth.currentUser;
        if (!user || !user.email) {
          return issue('form', 'Something went wrong. Please sign in again.');
        }

        try {
          const credential = EmailAuthProvider.credential(
            user.email,
            props.value.currentPassword,
          );
          await reauthenticateWithCredential(user, credential);
        } catch {
          return issue('currentPassword', 'Current password is incorrect');
        }

        try {
          await client.request('POST /me/password', {
            newPassword: props.value.password,
          });
        } catch (error) {
          const status =
            error && typeof error === 'object' && 'status' in error
              ? (error as { status: number }).status
              : 0;
          if (status === 400) {
            return issue('password', 'Password is too weak');
          }
          if (status === 401) {
            return issue('currentPassword', 'Current password is incorrect');
          }
          return issue('form', "Couldn't complete the update. Please try again.");
        }

        try {
          const credential = await signInWithEmailAndPassword(
            auth,
            user.email,
            props.value.password,
          );
          await credential.user.getIdToken(true);
        } catch (error) {
          console.error(error);
          toast.success('Password updated. Please sign in again.');
          await signOut(auth);
          await navigate('/signin');
          return undefined;
        }

        await navigate('/');
        return undefined;
      },
    },
  });

  return (
    <AuthLayout
      className="bg-[#F8F8F8]"
      title="Set a new password"
      subtitle="For security, please replace the temporary password before continuing."
    >
      <CardContent className="px-0">
        <form
          className="mt-6 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.Field
            name="currentPassword"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="current-password">
                  Current password
                </Label>
                <PasswordInput
                  id="current-password"
                  autoComplete="current-password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="password"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="new-password">
                  New password
                </Label>
                <PasswordInput
                  id="new-password"
                  autoComplete="new-password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="confirmPassword"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="confirm-password">
                  Confirm new password
                </Label>
                <PasswordInput
                  id="confirm-password"
                  autoComplete="new-password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <TheButton
                type="submit"
                loading={isSubmitting}
                disabled={!canSubmit}
                className="w-full cursor-pointer"
              >
                Save password
              </TheButton>
            )}
          />
          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </AuthLayout>
  );
}
